/**
 * A startup's legal and finance tab (F5c).
 *
 * The container half of the split plan §3.1 requires: the panel and form are
 * presentational and shared, and this supplies the data.
 *
 * A 409 gets the conflict dialog rather than an inline retry — "try again" is
 * wrong advice when a colleague has already saved, and the remedy is to reload
 * and see what they changed (PRD §6.2). This record is filled in by a finance
 * team and a deal lead from different sources, so that collision is the likely
 * one rather than the exotic one.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.9, PRD §5.11
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SaveLegalFinanceRequest } from '@nexus/contracts';

import { ConflictDialog } from '../../../components/domain/ConflictDialog';
import { LegalFinanceForm } from '../../../components/domain/LegalFinanceForm';
import { OnboardingReadiness } from '../../../components/domain/OnboardingReadiness';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Kicker } from '../../../components/ui/Kicker';
import { Note } from '../../../components/ui/Note';
import { useAuth } from '../../../lib/auth-context';
import { useLegalFinance, useSaveLegalFinance } from '../../../lib/legal-finance.queries';
import { useConflict } from '../../../lib/use-conflict';

export interface StartupLegalFinanceProps {
  startupId: string;
}

export function StartupLegalFinance({
  startupId,
}: StartupLegalFinanceProps): React.JSX.Element {
  const { t } = useTranslation();
  const { can } = useAuth();

  const { data, isPending, isError, refetch } = useLegalFinance(startupId);
  const save = useSaveLegalFinance(startupId);
  const { conflict, reportIfConflict, clear: clearConflict } = useConflict();
  const [error, setError] = useState<string | undefined>(undefined);

  if (isPending) {
    return (
      <p role="status" className="text-body">
        {t('state.loading')}
      </p>
    );
  }

  /**
   * A 403 is the expected answer for a viewer, not a fault.
   *
   * This tab is EDITOR-gated on the server because it holds registered
   * addresses and tax identifiers, so a viewer reaching it should be told why
   * rather than shown a generic error.
   */
  if (isError) {
    return (
      <Card>
        <CardBody>
          <p role="alert" className="text-body text-sm">
            {can('EDITOR') ? t('errors.generic') : t('legalFinance.editorOnly')}
          </p>
        </CardBody>
      </Card>
    );
  }

  const onSave = (body: SaveLegalFinanceRequest): void => {
    setError(undefined);
    save.mutate(body, {
      onError: (cause: unknown) => {
        if (reportIfConflict(cause)) return;
        setError(t('legalFinance.saveFailed'));
      },
    });
  };

  return (
    <>
      <div className="gap-s3 flex flex-col">
        {/* Readiness first. The failure this exists to prevent is finding out
            on the day an invoice is due, so the gaps lead. */}
        <Card>
          <CardHead>
            <Kicker as="h2">{t('legalFinance.readiness.heading')}</Kicker>
          </CardHead>
          <CardBody>
            <OnboardingReadiness record={data} />
          </CardBody>
        </Card>

        <Card>
          <CardHead>
            <Kicker as="h2">{t('legalFinance.heading')}</Kicker>
          </CardHead>
          <CardBody>
            {/* Stated in the interface, not just absent from the schema: a
                reader wondering where to put an IBAN needs an answer, and
                "there is nowhere" is the answer (PRD §5.11). */}
            <Note className="mb-s3">{t('legalFinance.noBankDetails')}</Note>

            <LegalFinanceForm
              record={data}
              isSaving={save.isPending}
              canEdit={can('EDITOR')}
              onSave={onSave}
              {...(error === undefined ? {} : { error })}
            />
          </CardBody>
        </Card>
      </div>

      <ConflictDialog
        open={conflict !== null}
        {...(conflict?.submittedVersion === undefined
          ? {}
          : { submittedVersion: conflict.submittedVersion })}
        onDismiss={clearConflict}
        onReload={() => {
          clearConflict();
          void refetch();
        }}
      />
    </>
  );
}
